import {
  Anchor,
  Center,
  Text,
  Paper,
  Button,
  PasswordInput,
  Group,
  Card,
  Tooltip,
  Modal,
  Accordion,
  Box,
  ActionIcon,
  Loader,
} from '@mantine/core';
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import * as modelViewer from '@google/model-viewer';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getJson, isApiError } from '@/services/httpClient';
import './tempViewer.css';
import {
  IconEye,
  IconQrcode,
  IconQrcodeOff,
  IconShare,
  IconList,
  IconTag,
  IconBoxOff,
  IconMaximize,
  IconArrowsMinimize,
  IconRectangle,
  IconX,
  IconFileDescription,
  IconStar,
  IconStarFilled,
  IconZoomReset,
  IconCamera,
  IconPencil,
  IconEyeOff,
  IconArrowsDiff,
} from '@tabler/icons-react';
import { UserProfile } from '../store/Store';
import {
  userPath,
  cdnCompanyLogos,
  websiteDomain,
  networkUrl,
  clientRoutes,
} from '../ApiPaths';
import { pageWrapper } from '@/Styles';
import { PENDING_PRODUCT_STAR_STORAGE_KEY } from '@/constants/storageKeys';
import type { IProduct, LinkShareMode } from '@/types/Product.model';
import {
  getProductById,
  starProduct,
  unstarProduct,
  unlockProductLinkShare,
  isProductLockedResponse,
  withProductShareQuery,
} from '@/Api';
import { useStore } from '@/store/Store';
import {
  MAX_COMPARE,
  toCompareSelectionItem,
  useCompareStore,
} from '@/store/compareStore';
import { ProductShareModal } from '@/components/ProductShareModal/ProductShareModal';
import { ProductQRCodeDialog } from '@/components/ProductShare';
import { ProductCardOverlayChip } from '@/components/badges';
// Temporarily hidden — categories UI disabled
// import { CategoryBadge } from '@/components/badges/CategoryBadge';
import {
  downloadModelViewerScreenshot,
} from '@/utils/productPreviewImage';
import { useTranslation } from 'react-i18next';
import { DataViewer } from '../components/DataViewer';
import { Annotation, ModelViewerSettings } from '@/types/Annotation.model';
import {
  AnnotationNavigator,
  AnnotationNavigatorRef,
  useAnnotationBarPortalTargetStyle,
  AnnotationImageModal,
  AnnotationProductModal,
  AnnotationHotspot,
  AnnotationInfoBox,
} from '@/components/Annotations';
import { AnimationSelector } from '@/components/AnimationSelector/AnimationSelector';
import {
  DEFAULT_MODEL_VIEWER_PRESENTATION,
  presentationSettingsToAttrs,
  presentationSettingsToViewerStyle,
  resolvePresentationSettings,
  type ModelViewerPresentationSettings,
} from '@/constants/modelViewerPresentation';
import { MODEL_VIEWER_RELAXED_ZOOM_ATTRS } from '@/utils/annotationConstants';
import { useAnnotationClickOutside } from '@/hooks/useAnnotationClickOutside';
import { useAnnotationClick } from '@/hooks/useAnnotationClick';
import { hasVisibleProductDescription } from '@/utils/productDescriptionHtml';
import { ProductDescriptionHtml } from '@/components/ProductDescriptionHtml/ProductDescriptionHtml';
import { RelatedProductsSection } from '@/components/RelatedProductsSection';
import { extractProductTags } from '@/utils/tags';
import { useAnnotationPositionUpdate } from '@/hooks/useAnnotationPositionUpdate';
import { useARButtonVisibility } from '@/hooks/useARButtonVisibility';
import { useModelViewerDynamicZoom } from '@/hooks/useModelViewerDynamicZoom';
import { glbZoomSrc, productUsdzUrl } from '@/utils/productModelUrl';
import { ModelViewerResetZoomButton } from '@/components/ModelViewer/ModelViewerResetZoomButton';
import { ModelViewerScreenshotButton } from '@/components/ModelViewer/ModelViewerScreenshotButton';
import { ModelViewerHelpButton } from '@/components/ModelViewer/ModelViewerHelpButton';
import { ViewerArButton, ViewerPlayPauseButton } from '@/components/ModelViewer/ViewerControlButton';
import {
  ModelViewerHelpOverlay,
  type ModelViewerHelpFeatures,
} from '@/components/ModelViewer/ModelViewerHelpOverlay';
import { ModelViewerPresentationPanel } from '@/components/ModelViewer/ModelViewerPresentationPanel';
import { resetModelViewerCamera } from '@/utils/modelViewerZoom';
import { cycleModelViewerAnimation } from '@/utils/modelViewerAnimation';
import { isTypingKeyboardTarget } from '@/utils/keyboardShortcuts';
import { useBrandForeground } from '@/utils/brandForeground';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';
import { useDeviceDetection } from '@/hooks/useDeviceDetection';
import Session, { useSessionContext } from 'supertokens-auth-react/recipe/session';
import { notifications } from '@mantine/notifications';

const getQRCodeUrl = (currentUrl: string) => {
  const environment = import.meta.env.MODE;
  return environment === 'production'
    ? currentUrl
    : currentUrl.replace('http://localhost', networkUrl);
};

function getFullscreenElement(): Element | null {
  const d = document as Document & {
    webkitFullscreenElement?: Element | null;
    mozFullScreenElement?: Element | null;
  };
  return document.fullscreenElement || d.webkitFullscreenElement || d.mozFullScreenElement || null;
}

async function toggleViewerFullscreen(container: HTMLElement | null): Promise<void> {
  if (!container) return;
  const fs = getFullscreenElement();
  const el = container as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void> | void;
    mozRequestFullScreen?: () => Promise<void> | void;
  };
  const doc = document as Document & {
    webkitExitFullscreen?: () => void;
    mozCancelFullScreen?: () => void;
  };
  try {
    if (fs === container) {
      if (document.exitFullscreen) await document.exitFullscreen();
      else if (doc.webkitExitFullscreen) doc.webkitExitFullscreen();
      else if (doc.mozCancelFullScreen) doc.mozCancelFullScreen();
    } else if (container.requestFullscreen) {
      await container.requestFullscreen();
    } else if (el.webkitRequestFullscreen) {
      await Promise.resolve(el.webkitRequestFullscreen());
    } else if (el.mozRequestFullScreen) {
      await Promise.resolve(el.mozRequestFullScreen());
    }
  } catch (err) {
    console.error('Fullscreen request failed', err);
  }
}

type InteractionBarProps = {
  toggleQR: () => void;
  showQR: boolean;
  corporateColor: string;
  /** 3D layout controls (full screen + theater); hidden when no GLB viewer */
  showViewerLayoutControls?: boolean;
  theaterMode?: boolean;
  onToggleTheater?: () => void;
  onToggleFullscreen?: () => void;
  isFullscreen?: boolean;
  /** Open unified share dialog */
  showShareLink?: boolean;
  shareLinkTooltip?: string;
  onShareLink?: () => void;
  showCompare?: boolean;
  compareSelected?: boolean;
  onCompare?: () => void;
  showResetView?: boolean;
  onResetView?: () => void;
  showScreenshot?: boolean;
  onScreenshot?: () => void;
  screenshotBusy?: boolean;
};

function InteractionBar({
  showQR,
  toggleQR,
  corporateColor,
  showViewerLayoutControls = false,
  theaterMode = false,
  onToggleTheater,
  onToggleFullscreen,
  isFullscreen = false,
  showShareLink = false,
  shareLinkTooltip = '',
  onShareLink,
  showCompare = false,
  compareSelected = false,
  onCompare,
  showResetView = false,
  onResetView,
  showScreenshot = false,
  onScreenshot,
  screenshotBusy = false,
}: InteractionBarProps) {
  const { t } = useTranslation();
  const brandIconColor = useBrandForeground(corporateColor);
  const compareTooltip = compareSelected
    ? t('marketplace.compare.remove')
    : t('marketplace.compare.compareAction');

  return (
    <Group gap="xs" style={{ marginLeft: 'auto' }}>
      {showShareLink && onShareLink && (
        <Tooltip label={shareLinkTooltip || t('productViewer.linkShare.shareTooltip')}>
          <Button
            variant="subtle"
            size="sm"
            color={brandIconColor}
            p={8}
            style={{ minWidth: 'unset', color: brandIconColor }}
            onClick={onShareLink}
            aria-label={shareLinkTooltip || t('productViewer.linkShare.shareTooltip')}
          >
            <IconShare size={18} />
          </Button>
        </Tooltip>
      )}

      {showCompare && onCompare && (
        <Tooltip label={compareTooltip}>
          <Button
            variant="subtle"
            size="sm"
            color={brandIconColor}
            p={8}
            style={{ minWidth: 'unset', color: brandIconColor }}
            onClick={onCompare}
            aria-label={compareTooltip}
          >
            <IconArrowsDiff size={18} stroke={1.75} />
          </Button>
        </Tooltip>
      )}

      {showResetView && onResetView && (
        <Tooltip label={t('modelViewer.resetZoom')}>
          <Button
            variant="subtle"
            size="sm"
            color={brandIconColor}
            p={8}
            style={{ minWidth: 'unset', color: brandIconColor }}
            onClick={onResetView}
            onPointerDown={(event: React.PointerEvent) => event.stopPropagation()}
            data-annotation-keep-open
            aria-label={t('modelViewer.resetZoom')}
          >
            <IconZoomReset size={18} stroke={1.75} />
          </Button>
        </Tooltip>
      )}

      {showScreenshot && onScreenshot && (
        <Tooltip label={t('modelViewer.screenshot')}>
          <Button
            variant="subtle"
            size="sm"
            color={brandIconColor}
            p={8}
            style={{ minWidth: 'unset', color: brandIconColor }}
            onClick={onScreenshot}
            onPointerDown={(event: React.PointerEvent) => event.stopPropagation()}
            data-annotation-keep-open
            disabled={screenshotBusy}
            aria-label={t('modelViewer.screenshot')}
          >
            {screenshotBusy ? <Loader size={14} color="gray" /> : <IconCamera size={18} stroke={1.75} />}
          </Button>
        </Tooltip>
      )}

      {showViewerLayoutControls && onToggleTheater && (
        <Tooltip
          label={
            theaterMode
              ? t('marketplace.productViewer.exitTheater')
              : t('marketplace.productViewer.theater')
          }
        >
          <Button
            variant="subtle"
            size="sm"
            color={brandIconColor}
            p={8}
            style={{ minWidth: 'unset', color: brandIconColor }}
            onClick={onToggleTheater}
          >
            <IconRectangle size={18} />
          </Button>
        </Tooltip>
      )}

      {showViewerLayoutControls && onToggleFullscreen && (
        <Tooltip
          label={
            isFullscreen
              ? t('marketplace.productViewer.exitFullscreen')
              : t('marketplace.productViewer.fullscreen')
          }
        >
          <Button
            variant="subtle"
            size="sm"
            color={brandIconColor}
            p={8}
            style={{ minWidth: 'unset', color: brandIconColor }}
            onClick={onToggleFullscreen}
          >
            {isFullscreen ? <IconArrowsMinimize size={18} /> : <IconMaximize size={18} />}
          </Button>
        </Tooltip>
      )}

      <Tooltip label={showQR ? t('marketplace.qr.hide') : t('marketplace.qr.show')}>
        <Button
          variant="subtle"
          size="sm"
          color={brandIconColor}
          p={8}
          style={{ minWidth: 'unset', color: brandIconColor }}
          onClick={toggleQR}
        >
          {showQR ? <IconQrcodeOff size={18} /> : <IconQrcode size={18} />}
        </Button>
      </Tooltip>
    </Group>
  );
}

export function ProductViewer() {
  const modelViewerRef = useRef<modelViewer.ModelViewerElement>(null);
  const annotationNavigatorRef = useRef<AnnotationNavigatorRef>(null);
  const [productOwner, setProductOwner] = useState<Partial<UserProfile>>();
  const [isPlaying, setIsPlaying] = useState(true);
  const [showQRCode, setShowQRCode] = useState(false);
  const [showARInfo, setShowARInfo] = useState(false);
  const deviceInfo = useDeviceDetection();
  const isDesktop = deviceInfo.isDesktop;
  const [product, setProduct] = useState<Partial<IProduct>>({
    productName: '',
    productPage: '',
    owningUser_id: '',
    productImage: '',
    availableFormats: [],
    views: 0,
  });
  /** 'idle' before fetch, 'loading' during, 'success' when product loaded, 'linkLocked' when password link without cookie, 'unavailable' when not found or error */
  const [productLoadStatus, setProductLoadStatus] = useState<
    'idle' | 'loading' | 'success' | 'unavailable' | 'linkLocked'
  >('idle');
  const [linkShareLockedInfo, setLinkShareLockedInfo] = useState<{
    _id: string;
    productName?: string;
    linkShareMode: LinkShareMode;
    shareExpired?: boolean;
  } | null>(null);
  const [unlockPassword, setUnlockPassword] = useState('');
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [shareModalOpened, setShareModalOpened] = useState(false);
  const userProfile = useStore((s) => s.userProfile);
  /** True when product exists but the 3D model asset (GLB) failed to load (e.g. 404, CORS, invalid file) */
  const [modelLoadFailed, setModelLoadFailed] = useState(false);
  /** Bumps model-viewer src after a one-shot session refresh retry on GLB error. */
  const [modelSrcNonce, setModelSrcNonce] = useState(0);
  const modelGlbRetryDoneRef = useRef(false);
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const shareToken = searchParams.get('share');

  const compareItems = useCompareStore((s) => s.items);
  const compareToggle = useCompareStore((s) => s.toggle);
  const compareSelected = Boolean(id && compareItems.some((i) => i._id === id));
  const compareFull = compareItems.length >= MAX_COMPARE && !compareSelected;

  const glbModelSrcBase = glbZoomSrc(product, id);
  const glbModelSrc = glbModelSrcBase
    ? withProductShareQuery(glbModelSrcBase, shareToken)
    : null;
  const glbModelSrcWithNonce =
    glbModelSrc && modelSrcNonce > 0
      ? `${glbModelSrc}${glbModelSrc.includes('?') ? '&' : '?'}_r=${modelSrcNonce}`
      : glbModelSrc;
  useModelViewerDynamicZoom(modelViewerRef, glbModelSrcWithNonce);
  const location = useLocation();
  const isIframe = location.pathname.includes('/embed');
  /** Embed iframe URL query: ?annotations=0&ar=0&playback=0&animations=0&reset=0&help=0&appearance=0 hides matching UI (defaults: all on). */
  const embedQueryFlags = useMemo(() => {
    if (!location.pathname.includes('/embed')) {
      return {
        showAnnotations: true,
        showAr: true,
        showPlayback: true,
        showAnimations: true,
        showReset: true,
        showHelp: true,
        showAppearance: true,
      };
    }
    const q = new URLSearchParams(location.search);
    const off = (key: string) => {
      const v = q.get(key);
      return v === '0' || v === 'false' || v === 'off';
    };
    return {
      showAnnotations: !off('annotations'),
      showAr: !off('ar'),
      showPlayback: !off('playback'),
      showAnimations: !off('animations'),
      showReset: !off('reset'),
      showHelp: !off('help'),
      showAppearance: !off('appearance'),
    };
  }, [location.pathname, location.search]);
  const [initialCameraOrbit, setInitialCameraOrbit] = useState<string>('');
  const [initialCameraTarget, setInitialCameraTarget] = useState<string>('');
  const [initialCameraFieldOfView, setInitialCameraFieldOfView] = useState<string>('');
  const [presentationSettings, setPresentationSettings] = useState<ModelViewerPresentationSettings>(
    DEFAULT_MODEL_VIEWER_PRESENTATION
  );
  const presentationAttrs = useMemo(
    () => presentationSettingsToAttrs(presentationSettings),
    [presentationSettings]
  );
  const presentationViewerStyle = useMemo(
    () => presentationSettingsToViewerStyle(presentationSettings),
    [presentationSettings]
  );
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [activeAnnotation, setActiveAnnotation] = useState<Annotation | null>(null);
  const [annotationsVisible, setAnnotationsVisible] = useState<boolean>(true);
  const [annotationPosition, setAnnotationPosition] = useState<{ x: number; y: number } | null>(
    null
  );
  const [imageModalOpened, setImageModalOpened] = useState(false);
  const [productModalOpened, setProductModalOpened] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<IProduct | null>(null);
  const descriptionBoxRef = useRef<HTMLDivElement>(null);
  const [hasAnimations, setHasAnimations] = useState<boolean>(false);
  const viewerStageRef = useRef<HTMLDivElement>(null);
  const [theaterMode, setTheaterMode] = useState(false);
  /** CSS enter class — lags one frame behind open so the scale/fade can run. */
  const [theaterEntered, setTheaterEntered] = useState(false);
  /** Enable transform transition only after the centered pre-scale frame is painted. */
  const [theaterAnimating, setTheaterAnimating] = useState(false);
  const theaterCloseTimerRef = useRef<number | null>(null);
  const THEATER_TRANSITION_MS = 240;
  const [isFullscreen, setIsFullscreen] = useState(false);
  const immersiveModelViewerStyle = useMemo((): React.CSSProperties => {
    if (!theaterMode && !isFullscreen) {
      return presentationViewerStyle;
    }
    if (presentationSettings.backgroundColor !== 'transparent') {
      return presentationViewerStyle;
    }
    return {
      ...presentationViewerStyle,
      backgroundColor: 'var(--mantine-color-body)',
      ['--poster-color' as string]: 'var(--mantine-color-body)',
    };
  }, [
    presentationViewerStyle,
    presentationSettings.backgroundColor,
    theaterMode,
    isFullscreen,
  ]);
  const { t } = useTranslation();
  const session = useSessionContext();
  const [starCount, setStarCount] = useState(0);
  const [starredByMe, setStarredByMe] = useState(false);
  const [starActionLoading, setStarActionLoading] = useState(false);
  const [starLoginModalOpened, setStarLoginModalOpened] = useState(false);
  const [helpOverlayOpened, setHelpOverlayOpened] = useState(false);
  const navigate = useNavigate();

  const handleCompareToggle = () => {
    if (!product || !id) return;
    const item = toCompareSelectionItem(product);
    if (!item) return;
    if (compareFull) {
      notifications.show({
        color: 'orange',
        message: t('marketplace.compare.maxReached', { max: MAX_COMPARE }),
      });
      return;
    }
    compareToggle(item);
  };

  const handleToggleStar = React.useCallback(async () => {
    if (!id || starActionLoading) return;
    if (session.loading) return;
    if (!session.doesSessionExist) {
      setStarLoginModalOpened(true);
      return;
    }
    setStarActionLoading(true);
    try {
      if (starredByMe) {
        const r = await unstarProduct(id);
        setStarCount(r.starCount);
        setStarredByMe(false);
      } else {
        const r = await starProduct(id);
        setStarCount(r.starCount);
        setStarredByMe(true);
      }
    } catch (e) {
      console.error(e);
      notifications.show({
        title: t('marketplace.productViewer.starError'),
        message: t('marketplace.productViewer.starErrorHint'),
        color: 'red',
      });
    } finally {
      setStarActionLoading(false);
    }
  }, [id, starredByMe, starActionLoading, session, t]);

  /** Mantine menus portal to body with default z ~1001; theater backdrop is 2000 and stage 2001 — raise chrome when immersive. */
  const viewerImmersiveChromeBaseZ = theaterMode || isFullscreen ? 2100 : undefined;
  const viewerChromeStackZ =
    viewerImmersiveChromeBaseZ != null ? viewerImmersiveChromeBaseZ + 30 : undefined;
  const annotationBarPortalStyle = useAnnotationBarPortalTargetStyle({
    zIndex: viewerChromeStackZ ?? 12,
  });
  const embedAnnotationBarPortalStyle = useAnnotationBarPortalTargetStyle({ zIndex: 12 });
  const viewerCloseChromeZ =
    viewerImmersiveChromeBaseZ != null ? viewerImmersiveChromeBaseZ + 150 : undefined;
  const viewerHelpOverlayZ = viewerCloseChromeZ != null ? viewerCloseChromeZ + 20 : 200;
  /** Browser fullscreen: overlays must portal into the fullscreen element; body stays behind the top layer. */
  const viewerStagePortalSelector = '#product-viewer-stage';
  const portalIntoViewerStage = isFullscreen;
  const immersiveOverlayZIndex = theaterMode || isFullscreen ? 5000 : undefined;

  const clearTheaterCloseTimer = React.useCallback(() => {
    if (theaterCloseTimerRef.current != null) {
      window.clearTimeout(theaterCloseTimerRef.current);
      theaterCloseTimerRef.current = null;
    }
  }, []);

  const openTheater = React.useCallback(() => {
    clearTheaterCloseTimer();
    setTheaterAnimating(false);
    setTheaterEntered(false);
    setTheaterMode(true);
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      setTheaterEntered(true);
      return;
    }
    // Paint centered + scaled-down frame with transition off, then animate scale only.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTheaterAnimating(true);
        setTheaterEntered(true);
      });
    });
  }, [clearTheaterCloseTimer]);

  const closeTheater = React.useCallback(() => {
    const el = viewerStageRef.current;
    if (el && getFullscreenElement() === el) {
      void toggleViewerFullscreen(el);
    }
    clearTheaterCloseTimer();
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    setTheaterAnimating(true);
    setTheaterEntered(false);
    if (reduceMotion) {
      setTheaterMode(false);
      setTheaterAnimating(false);
      return;
    }
    theaterCloseTimerRef.current = window.setTimeout(() => {
      setTheaterMode(false);
      setTheaterAnimating(false);
      theaterCloseTimerRef.current = null;
    }, THEATER_TRANSITION_MS);
  }, [clearTheaterCloseTimer]);

  useEffect(() => () => clearTheaterCloseTimer(), [clearTheaterCloseTimer]);

  const handleTagNavigate = React.useCallback(
    (tag: string) => {
      const q = new URLSearchParams();
      q.set('tags', tag);
      navigate({ pathname: clientRoutes.home, search: `?${q.toString()}` });
    },
    [navigate]
  );

  /* Temporarily hidden — categories UI disabled
  const handleCategoryNavigate = React.useCallback(
    (category: string) => {
      const q = new URLSearchParams();
      q.set('categories', category);
      navigate({ pathname: clientRoutes.home, search: `?${q.toString()}` });
    },
    [navigate]
  );
  */

  // Close description box when clicking outside
  useAnnotationClickOutside({
    descriptionBoxRef,
    activeAnnotation,
    onClose: () => {
      setActiveAnnotation(null);
      setAnnotationPosition(null);
      setImageModalOpened(false);
      setProductModalOpened(false);
      setSelectedProduct(null);
    },
    imageModalOpened,
    productModalOpened,
  });

  // Show/hide native AR button when model-viewer loads
  useARButtonVisibility({ modelViewerRef, isDesktop });

  useEffect(() => {
    const syncFs = () => {
      const el = viewerStageRef.current;
      const fs = getFullscreenElement();
      // Must require a real stage node: null === null would wrongly mark fullscreen "true" while loading.
      setIsFullscreen(Boolean(el && fs === el));
    };
    document.addEventListener('fullscreenchange', syncFs);
    document.addEventListener('webkitfullscreenchange', syncFs);
    document.addEventListener('mozfullscreenchange', syncFs);
    syncFs();
    return () => {
      document.removeEventListener('fullscreenchange', syncFs);
      document.removeEventListener('webkitfullscreenchange', syncFs);
      document.removeEventListener('mozfullscreenchange', syncFs);
    };
  }, [productLoadStatus]);

  useEffect(() => {
    if (!theaterMode && !isFullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !helpOverlayOpened) closeTheater();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [theaterMode, isFullscreen, closeTheater, helpOverlayOpened]);

  useEffect(() => {
    if (theaterMode) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [theaterMode]);

  // When product is loaded, listen for model-viewer load errors (e.g. GLB 404 or invalid)
  useEffect(() => {
    if (productLoadStatus !== 'success' || !product?.availableFormats?.includes('.glb')) return;
    const mv = modelViewerRef.current;
    if (!mv) return;
    const onError = () => {
      void (async () => {
        // One-shot: stale session can make /glb 502; refresh cookies and remount src.
        if (!modelGlbRetryDoneRef.current && (await Session.doesSessionExist())) {
          modelGlbRetryDoneRef.current = true;
          try {
            const ok = await Session.attemptRefreshingSession();
            if (ok) {
              setModelLoadFailed(false);
              setModelSrcNonce((n) => n + 1);
              return;
            }
          } catch {
            // fall through to failed state
          }
        }
        setModelLoadFailed(true);
      })();
    };
    mv.addEventListener('error', onError);
    return () => mv.removeEventListener('error', onError);
  }, [productLoadStatus, product?.availableFormats, modelSrcNonce]);

  // Check if model has animations
  useEffect(() => {
    const modelViewer = modelViewerRef.current;
    if (!modelViewer) return;

    const checkAnimations = () => {
      try {
        const animations = (modelViewer as any).availableAnimations || [];
        const validAnimations = animations.filter((name: string) => name && name.trim() !== '');
        setHasAnimations(validAnimations.length > 0);
      } catch (error) {
        console.error('Error checking animations:', error);
        setHasAnimations(false);
      }
    };

    const handleLoad = () => {
      setTimeout(checkAnimations, 100);
    };

    modelViewer.addEventListener('load', handleLoad);

    // Also check periodically in case animations load after the load event
    const interval = setInterval(() => {
      if (modelViewerRef.current) {
        checkAnimations();
      }
    }, 500);

    // Initial check
    checkAnimations();

    return () => {
      modelViewer.removeEventListener('load', handleLoad);
      clearInterval(interval);
    };
  }, [modelViewerRef]);

  const loadProductPage = React.useCallback(async () => {
    if (!id) {
      setProductLoadStatus('unavailable');
      return;
    }
    setProductLoadStatus('loading');
    setLinkShareLockedInfo(null);
    setUnlockError(null);
    setUnlockPassword('');
    setModelLoadFailed(false);
    modelGlbRetryDoneRef.current = false;
    setModelSrcNonce(0);

    const applyProduct = async (res: Awaited<ReturnType<typeof getProductById>>) => {
      if (isProductLockedResponse(res)) {
        setLinkShareLockedInfo({
          _id: res._id,
          productName: res.productName,
          linkShareMode: res.linkShareMode ?? 'password',
          shareExpired: Boolean(res.shareExpired),
        });
        setProductLoadStatus('linkLocked');
        if (res.owningUser_id) {
          getJson<Partial<UserProfile>>(userPath + res.owningUser_id)
            .then((userData) => setProductOwner(userData))
            .catch((error) => console.error(error));
        }
        return;
      }
      setProduct(res);
      setStarCount(res.starCount ?? 0);
      setStarredByMe(Boolean(res.starredByMe));
      setProductLoadStatus('success');
      if (res.modelViewerSettings) {
        try {
          const settings: ModelViewerSettings = JSON.parse(res.modelViewerSettings);
          if (settings.initialCameraOrbit) setInitialCameraOrbit(settings.initialCameraOrbit);
          if (settings.initialCameraTarget) setInitialCameraTarget(settings.initialCameraTarget);
          if (settings.initialCameraFieldOfView) {
            setInitialCameraFieldOfView(settings.initialCameraFieldOfView);
          }
          if (Array.isArray(settings.annotations)) setAnnotations(settings.annotations);
          setPresentationSettings(resolvePresentationSettings(settings));
        } catch (error) {
          console.error('Error parsing modelViewerSettings:', error);
        }
      }
      if (!res.owningUser_id) return;
      getJson<Partial<UserProfile>>(userPath + res.owningUser_id)
        .then((userData) => setProductOwner(userData))
        .catch((error) => console.error(error));
    };

    try {
      const res = await getProductById(id, { share: shareToken });
      await applyProduct(res);
    } catch (firstError) {
      // Stale client session + gateway/auth blip: refresh once, then retry.
      // Avoids "models broken until logout/login" for expired access tokens.
      const gatewayOrAuth =
        isApiError(firstError) &&
        [401, 502, 503, 504].includes(firstError.response.status);
      let refreshed = false;
      try {
        if (gatewayOrAuth && (await Session.doesSessionExist())) {
          refreshed = await Session.attemptRefreshingSession();
        }
      } catch {
        refreshed = false;
      }
      if (!refreshed) {
        console.error(firstError);
        setProductLoadStatus('unavailable');
        return;
      }
      try {
        const res = await getProductById(id, { share: shareToken });
        await applyProduct(res);
      } catch (retryError) {
        console.error(retryError);
        setProductLoadStatus('unavailable');
      }
    }
  }, [id, shareToken]);

  useEffect(() => {
    void loadProductPage();
  }, [loadProductPage]);

  const handleLinkShareUnlock = React.useCallback(async () => {
    if (!id) return;
    setUnlockLoading(true);
    setUnlockError(null);
    try {
      await unlockProductLinkShare(id, unlockPassword);
      await loadProductPage();
    } catch (e: unknown) {
      if (isApiError(e) && e.response.status === 401) {
        setUnlockError(t('productViewer.linkShare.wrongPassword'));
      } else if (
        isApiError(e) &&
        e.response.data &&
        typeof e.response.data === 'object' &&
        'message' in e.response.data
      ) {
        setUnlockError(String((e.response.data as { message?: string }).message));
      } else {
        setUnlockError(t('productViewer.linkShare.unlockFailed'));
      }
    } finally {
      setUnlockLoading(false);
    }
  }, [id, unlockPassword, loadProductPage, t]);

  // After login from "favorite this product", star once and clear pending flag from sessionStorage.
  useEffect(() => {
    if (session.loading) return;
    if (!session.doesSessionExist) return;
    if (productLoadStatus !== 'success' || !id) return;
    const pending = sessionStorage.getItem(PENDING_PRODUCT_STAR_STORAGE_KEY);
    if (pending !== id) return;

    let cancelled = false;
    (async () => {
      try {
        const r = await starProduct(id);
        sessionStorage.removeItem(PENDING_PRODUCT_STAR_STORAGE_KEY);
        if (cancelled) return;
        setStarCount(r.starCount);
        setStarredByMe(true);
        notifications.show({
          title: t('marketplace.productViewer.starAddedAfterLogin'),
          message: '',
          color: 'green',
        });
      } catch (e) {
        console.error(e);
        sessionStorage.removeItem(PENDING_PRODUCT_STAR_STORAGE_KEY);
        if (!cancelled) {
          notifications.show({
            title: t('marketplace.productViewer.starError'),
            message: t('marketplace.productViewer.starErrorHint'),
            color: 'red',
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session, productLoadStatus, id, t]);

  const toggleAnimation = React.useCallback(() => {
    if (!modelViewerRef.current) return;
    if (isPlaying) {
      modelViewerRef.current.pause();
    } else {
      modelViewerRef.current.play();
    }
    setIsPlaying((prev) => !prev);
  }, [isPlaying]);

  const handleAnnotationClick = useAnnotationClick({
    modelViewerRef,
    annotationNavigatorRef,
    setActiveAnnotation,
    setAnnotationPosition,
    onCloseModals: () => {
      setImageModalOpened(false);
      setProductModalOpened(false);
      setSelectedProduct(null);
    },
  });

  // Update annotation position when camera moves
  useAnnotationPositionUpdate({
    modelViewerRef,
    activeAnnotation,
    setAnnotationPosition,
  });

  const handleARClick = () => {
    // Desktop: Show QR code (no AR support)
    // Mobile/Tablet/VR: Use native AR (model-viewer's built-in AR button)
    if (deviceInfo.isDesktop) {
      setShowARInfo(false);
      setShowQRCode(true);
    }
  };

  // Get corporate color from productOwner, or use default
  const corporateColor = productOwner?.qrCodeColor || DEFAULT_QR_CODE_COLOR;

  /** Always point QR at the full product page (not /embed/...) so phones open AR-capable view */
  const qrCodeValue = useMemo(() => {
    if (!id) return getQRCodeUrl(typeof window !== 'undefined' ? window.location.href : '');
    const productUrl = `${websiteDomain}${clientRoutes.product.replace(':id', id)}`;
    return getQRCodeUrl(productUrl);
  }, [id]);

  /** Orbit alone is relative to camera target — persist target + FOV with preview for same shot on product page */
  const savedProductCameraAttrs = useMemo(
    () => ({
      ...(initialCameraOrbit && { 'camera-orbit': initialCameraOrbit }),
      ...(initialCameraTarget && { 'camera-target': initialCameraTarget }),
      ...(initialCameraFieldOfView && { 'field-of-view': initialCameraFieldOfView }),
    }),
    [initialCameraOrbit, initialCameraTarget, initialCameraFieldOfView]
  );

  const starActionTooltip = useMemo(() => {
    if (session.loading) return t('marketplace.productViewer.star');
    if (!session.doesSessionExist) return t('marketplace.productViewer.starLoginTooltip');
    return starredByMe
      ? t('marketplace.productViewer.unstar')
      : t('marketplace.productViewer.star');
  }, [session, starredByMe, t]);

  const isOwner = Boolean(
    productLoadStatus === 'success' &&
      !session.loading &&
      session.doesSessionExist &&
      userProfile._id &&
      product.owningUser_id &&
      userProfile._id === product.owningUser_id
  );

  const showProductViewerLayoutControls =
    productLoadStatus === 'success' &&
    Boolean(product?.availableFormats?.includes('.glb')) &&
    !modelLoadFailed;

  const showModelViewerHelp = showProductViewerLayoutControls && (!isIframe || embedQueryFlags.showHelp);

  const modelViewerHelpFeatures = useMemo((): ModelViewerHelpFeatures => {
    const shortcuts = {
      resetView: true,
      help: true,
      playPause: hasAnimations,
      annotationStep: annotations.length > 0,
      animationStep: hasAnimations,
      escape: true,
    } as const;

    if (isIframe) {
      return {
        resetView: embedQueryFlags.showReset,
        appearance: embedQueryFlags.showAppearance,
        annotations: embedQueryFlags.showAnnotations && annotations.length > 0,
        animations: embedQueryFlags.showAnimations && hasAnimations,
        ar: isDesktop && embedQueryFlags.showAr,
        shortcuts: embedQueryFlags.showHelp
          ? {
              resetView: embedQueryFlags.showReset,
              help: true,
              playPause: embedQueryFlags.showAnimations && hasAnimations,
              annotationStep: embedQueryFlags.showAnnotations && annotations.length > 0,
              animationStep: embedQueryFlags.showAnimations && hasAnimations,
              escape: true,
            }
          : undefined,
      };
    }

    return {
      resetView: showProductViewerLayoutControls,
      appearance: showProductViewerLayoutControls,
      annotations: annotations.length > 0,
      animations: hasAnimations,
      ar: isDesktop,
      theater: showProductViewerLayoutControls,
      fullscreen: showProductViewerLayoutControls,
      qr: true,
      share: isOwner,
      favorites: true,
      shortcuts,
    };
  }, [
    isIframe,
    embedQueryFlags.showAnnotations,
    embedQueryFlags.showAnimations,
    embedQueryFlags.showAr,
    embedQueryFlags.showReset,
    embedQueryFlags.showAppearance,
    embedQueryFlags.showHelp,
    annotations.length,
    hasAnimations,
    isDesktop,
    showProductViewerLayoutControls,
    isOwner,
  ]);

  const handleResetProductView = React.useCallback(() => {
    const mv = modelViewerRef.current;
    if (!mv) return;
    resetModelViewerCamera(mv, {
      cameraOrbit: initialCameraOrbit,
      cameraTarget: initialCameraTarget,
      cameraFieldOfView: initialCameraFieldOfView,
    });
  }, [initialCameraOrbit, initialCameraTarget, initialCameraFieldOfView]);

  useEffect(() => {
    if (!showProductViewerLayoutControls) return;

    const onKey = (e: KeyboardEvent) => {
      if (helpOverlayOpened) return;
      if (isTypingKeyboardTarget(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const key = e.key.toLowerCase();

      if ((key === '?' || key === 'h') && showModelViewerHelp) {
        e.preventDefault();
        setHelpOverlayOpened(true);
        return;
      }

      if (key === 'r' && (!isIframe || embedQueryFlags.showReset)) {
        e.preventDefault();
        handleResetProductView();
        return;
      }

      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        const canStepAnnotations =
          annotations.length > 0 && (!isIframe || embedQueryFlags.showAnnotations);
        if (!canStepAnnotations) return;
        e.preventDefault();
        if (e.key === 'ArrowRight') {
          annotationNavigatorRef.current?.navigateNext();
        } else {
          annotationNavigatorRef.current?.navigatePrevious();
        }
        return;
      }

      if (e.key === ']' || e.key === '[') {
        const canStepAnimations = hasAnimations && (!isIframe || embedQueryFlags.showAnimations);
        if (!canStepAnimations) return;
        e.preventDefault();
        cycleModelViewerAnimation(modelViewerRef.current, e.key === ']' ? 1 : -1);
        setIsPlaying(true);
        return;
      }

      if (e.key === ' ' || key === 'spacebar') {
        if (!hasAnimations) return;
        if (isIframe && !embedQueryFlags.showAnimations) return;
        e.preventDefault();
        toggleAnimation();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    showProductViewerLayoutControls,
    helpOverlayOpened,
    showModelViewerHelp,
    isIframe,
    embedQueryFlags.showReset,
    embedQueryFlags.showAnimations,
    embedQueryFlags.showAnnotations,
    hasAnimations,
    annotations.length,
    handleResetProductView,
    toggleAnimation,
  ]);

  const [screenshotBusy, setScreenshotBusy] = useState(false);

  const handleProductScreenshot = React.useCallback(async () => {
    const mv = modelViewerRef.current;
    if (!mv || screenshotBusy) return;
    setScreenshotBusy(true);
    try {
      await downloadModelViewerScreenshot(mv, {
        includeAnnotations: true,
        fileName: product?.productName,
      });
    } catch (error) {
      console.error('Model viewer screenshot failed:', error);
      notifications.show({
        title: t('modelViewer.screenshotErrorTitle'),
        message: t('modelViewer.screenshotError'),
        color: 'red',
      });
    } finally {
      setScreenshotBusy(false);
    }
  }, [screenshotBusy, product?.productName, t]);

  // Idle: no id means show unavailable; with id show loader until fetch resolves
  if (productLoadStatus === 'idle') {
    if (!id) {
      return (
        <Center style={pageWrapper}>
          <Paper
            p="xl"
            radius="md"
            withBorder
            style={{
              maxWidth: '420px',
              textAlign: 'center',
              background: 'var(--mantine-color-body)',
            }}
          >
            <IconBoxOff
              size={48}
              style={{ margin: '0 auto 16px', display: 'block', opacity: 0.6 }}
            />
            <Text fw={600} size="lg" mb="xs">
              {t('productViewer.productUnavailableTitle')}
            </Text>
            <Text size="sm" c="dimmed" maw={360} mx="auto">
              {t('productViewer.productUnavailableHint')}
            </Text>
          </Paper>
        </Center>
      );
    }
    return (
      <Center style={pageWrapper}>
        <Loader size="lg" color="gray" />
      </Center>
    );
  }

  // Loading: show subtle loader (no empty window)
  if (productLoadStatus === 'loading') {
    return (
      <Center style={pageWrapper}>
        <Loader size="lg" color="gray" />
      </Center>
    );
  }

  if (productLoadStatus === 'linkLocked' && linkShareLockedInfo && id) {
    const isRestrictedLink = linkShareLockedInfo.linkShareMode === 'restricted';
    const isPasswordLink = linkShareLockedInfo.linkShareMode === 'password';
    const shareExpired = Boolean(linkShareLockedInfo.shareExpired);
    const returnPath = `${location.pathname}${location.search}`;
    return (
      <Center style={pageWrapper}>
        <Paper
          p="xl"
          radius="md"
          withBorder
          style={{
            maxWidth: '420px',
            width: '100%',
            textAlign: 'center',
            background: 'var(--mantine-color-body)',
          }}
        >
          <Text fw={600} size="lg" mb="xs">
            {shareExpired
              ? t('productViewer.linkShare.shareExpiredTitle')
              : isRestrictedLink
                ? t('productViewer.linkShare.restrictedTitle')
                : t('productViewer.linkShare.lockedTitle')}
          </Text>
          <Text size="sm" c="dimmed" mb="md">
            {shareExpired
              ? t('productViewer.linkShare.shareExpiredSubtitle')
              : isRestrictedLink
                ? linkShareLockedInfo.productName
                  ? t('productViewer.linkShare.restrictedSubtitleNamed', {
                      name: linkShareLockedInfo.productName,
                    })
                  : t('productViewer.linkShare.restrictedSubtitle')
                : linkShareLockedInfo.productName
                  ? t('productViewer.linkShare.lockedSubtitleNamed', {
                      name: linkShareLockedInfo.productName,
                    })
                  : t('productViewer.linkShare.lockedSubtitle')}
          </Text>
          {isPasswordLink ? (
            <>
              <PasswordInput
                label={t('productViewer.linkShare.passwordLabel')}
                value={unlockPassword}
                onChange={(e) => setUnlockPassword(e.currentTarget.value)}
                mb="sm"
                autoComplete="current-password"
              />
              {unlockError ? (
                <Text size="sm" c="red" mb="sm">
                  {unlockError}
                </Text>
              ) : null}
              <Button
                fullWidth
                color={corporateColor}
                loading={unlockLoading}
                onClick={() => void handleLinkShareUnlock()}
              >
                {t('productViewer.linkShare.unlock')}
              </Button>
            </>
          ) : isRestrictedLink ? (
            !session.loading && !session.doesSessionExist ? (
              <Button
                fullWidth
                color={corporateColor}
                onClick={() =>
                  navigate(`${clientRoutes.login}?returnTo=${encodeURIComponent(returnPath)}`)
                }
              >
                {t('productViewer.linkShare.restrictedSignIn')}
              </Button>
            ) : (
              <Text size="sm" c="dimmed">
                {t('productViewer.linkShare.restrictedLoggedInNoAccess')}
              </Text>
            )
          ) : null}
        </Paper>
      </Center>
    );
  }

  // Product not found, not accessible, or model asset failed to load: calm message instead of empty viewer
  if (productLoadStatus === 'unavailable' || modelLoadFailed) {
    return (
      <Center style={pageWrapper}>
        <Paper
          p="xl"
          radius="md"
          withBorder
          style={{
            maxWidth: '420px',
            textAlign: 'center',
            background: 'var(--mantine-color-body)',
          }}
        >
          <IconBoxOff size={48} style={{ margin: '0 auto 16px', display: 'block', opacity: 0.6 }} />
          <Text fw={600} size="lg" mb="xs">
            {t('productViewer.productUnavailableTitle')}
          </Text>
          <Text size="sm" c="dimmed" maw={360} mx="auto">
            {t('productViewer.productUnavailableHint')}
          </Text>
        </Paper>
      </Center>
    );
  }

  const imageModal = (
    <AnnotationImageModal
      opened={imageModalOpened}
      onClose={() => setImageModalOpened(false)}
      annotation={activeAnnotation}
      portalTarget={portalIntoViewerStage ? viewerStagePortalSelector : undefined}
      modalZIndex={immersiveOverlayZIndex}
    />
  );

  const productModal = (
    <AnnotationProductModal
      opened={productModalOpened}
      onClose={() => {
        setProductModalOpened(false);
        setSelectedProduct(null);
      }}
      product={selectedProduct}
      portalTarget={portalIntoViewerStage ? viewerStagePortalSelector : undefined}
      modalZIndex={immersiveOverlayZIndex}
    />
  );

  const qrModal = (
    <ProductQRCodeDialog
      opened={showQRCode}
      onClose={() => {
        setShowQRCode(false);
        setShowARInfo(false);
      }}
      url={qrCodeValue}
      accentColor={corporateColor}
      logoImage={
        productOwner?.companyLogo ? `${cdnCompanyLogos}${productOwner.companyLogo}` : undefined
      }
      showStyleToggle
      showArInfo={showARInfo}
      zIndex={immersiveOverlayZIndex}
      portalTarget={portalIntoViewerStage ? viewerStagePortalSelector : undefined}
    />
  );

  if (isIframe)
    return (
      <>
        {imageModal}
        {productModal}
        {qrModal}
        {/* Fill the host iframe viewport — fixed 600×300 clipped bottom-right controls on small/mobile embeds */}
        <Box
          style={{
            position: 'fixed',
            inset: 0,
            width: '100%',
            height: '100%',
            margin: 0,
            paddingTop: 'env(safe-area-inset-top, 0px)',
            paddingRight: 'env(safe-area-inset-right, 0px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            paddingLeft: 'env(safe-area-inset-left, 0px)',
            boxSizing: 'border-box',
            overflow: 'hidden',
          }}
        >
          {/* Match non-embed layout: bar is portaled into a full-viewer-width box so left:50% centers on the model, not on the small bottom-right button row */}
          <Box style={{ position: 'relative', width: '100%', height: '100%' }}>
            <Box id="product-viewer-annotation-bar" style={embedAnnotationBarPortalStyle} />
            {embedQueryFlags.showReset ? (
              <>
                <ModelViewerResetZoomButton
                  modelViewerRef={modelViewerRef}
                  cameraDefaults={{
                    cameraOrbit: initialCameraOrbit,
                    cameraTarget: initialCameraTarget,
                    cameraFieldOfView: initialCameraFieldOfView,
                  }}
                />
                <ModelViewerScreenshotButton
                  modelViewerRef={modelViewerRef}
                  fileName={product?.productName}
                />
              </>
            ) : null}
            <model-viewer
              ref={modelViewerRef}
              style={{
                width: '100%',
                height: '100%',
                minHeight: 'min(50vh, 560px)',
                display: 'block',
                ...presentationViewerStyle,
              }}
              src={
                product?.availableFormats?.includes('.glb') && glbModelSrcWithNonce
                  ? glbModelSrcWithNonce
                  : ''
              }
              ios-src={
                product?.availableFormats?.includes('.usdz') && id
                  ? withProductShareQuery(productUsdzUrl(id), shareToken)
                  : ''
              }
              {...(embedQueryFlags.showAr
                ? {
                    ar: true,
                    'ar-modes': 'webxr scene-viewer quick-look',
                    'ar-scale': 'auto',
                    'ar-placement': 'floor',
                  }
                : {})}
              interaction-prompt="auto"
              interaction-prompt-style="basic"
              {...presentationAttrs}
              camera-controls
              {...MODEL_VIEWER_RELAXED_ZOOM_ATTRS}
              touch-action="pan-y"
              enable-pan
              autoplay // it actually is correct, do not fix
              {...savedProductCameraAttrs}
            >
              {embedQueryFlags.showAnnotations &&
                annotationsVisible &&
                annotations.map((annotation) => (
                  <AnnotationHotspot
                    key={annotation.id}
                    annotation={annotation}
                    onClick={handleAnnotationClick}
                  />
                ))}
            </model-viewer>

            {/* Active Annotation Info Box for iframe - Positioned next to clicked pivot */}
            {embedQueryFlags.showAnnotations && activeAnnotation && annotationPosition && (
              <AnnotationInfoBox
                activeAnnotation={activeAnnotation}
                annotationPosition={annotationPosition}
                descriptionBoxRef={descriptionBoxRef}
                backgroundColor={corporateColor}
                onProductClick={(loaded) => {
                  setSelectedProduct(loaded);
                  setProductModalOpened(true);
                }}
                onImageClick={() => setImageModalOpened(true)}
              />
            )}

            {/* Presentation + annotation + animation controls */}
            <Box
              style={{
                position: 'absolute',
                bottom: '16px',
                right: '116px',
                zIndex: 12,
                display: 'flex',
                flexDirection: 'row',
                gap: '8px',
                alignItems: 'center',
              }}
            >
              {embedQueryFlags.showAppearance ? (
                <ModelViewerPresentationPanel
                  settings={presentationSettings}
                  onChange={setPresentationSettings}
                  modelViewerRef={modelViewerRef}
                  color={corporateColor}
                  inline
                />
              ) : null}
              {embedQueryFlags.showAnnotations && (
                <AnnotationNavigator
                  ref={annotationNavigatorRef}
                  annotations={annotations}
                  modelViewerRef={modelViewerRef}
                  color="#000000"
                  onAnnotationChange={setActiveAnnotation}
                  onAnnotationsVisibleChange={setAnnotationsVisible}
                  hasAnimationSelector={hasAnimations && embedQueryFlags.showAnimations}
                  inline
                  barPortalTargetId="product-viewer-annotation-bar"
                />
              )}
              {embedQueryFlags.showAnimations && hasAnimations && (
                <AnimationSelector modelViewerRef={modelViewerRef} color="#000000" />
              )}
            </Box>

            {/* Play / pause — same overlay as full product page (sits between AR and annotation row) */}
            {embedQueryFlags.showPlayback ? (
              <ViewerPlayPauseButton
                accentColor={corporateColor}
                isPlaying={isPlaying}
                onClick={toggleAnimation}
                className="play-pause-button"
                style={{
                  position: 'absolute',
                  bottom: '16px',
                  right: isDesktop && embedQueryFlags.showAr ? '68px' : '16px',
                  zIndex: 11,
                }}
              />
            ) : null}

            {/* Desktop embed: same control as product page AR button (opens QR on desktop) */}
            {isDesktop && embedQueryFlags.showAr && (
              <div
                style={{
                  position: 'absolute',
                  bottom: '16px',
                  right: '16px',
                  zIndex: 14,
                  display: 'flex',
                  gap: '8px',
                }}
                className="desktop-ar-buttons"
              >
                <ViewerArButton
                  accentColor={DEFAULT_QR_CODE_COLOR}
                  onClick={handleARClick}
                  ariaLabel={t('marketplace.qr.scan')}
                />
              </div>
            )}

            {showModelViewerHelp && (
              <>
                <ModelViewerHelpButton
                  onClick={() => setHelpOverlayOpened(true)}
                  zIndex={viewerChromeStackZ ?? 12}
                />
                <ModelViewerHelpOverlay
                  opened={helpOverlayOpened}
                  onClose={() => setHelpOverlayOpened(false)}
                  onResetCamera={handleResetProductView}
                  features={modelViewerHelpFeatures}
                  zIndex={viewerHelpOverlayZ}
                />
              </>
            )}
          </Box>
        </Box>
      </>
    );

  return (
    <>
      {imageModal}
      {productModal}
      <Center
        style={{
          ...pageWrapper,
          padding: '0 0',
          maxWidth: '100%',
        }}
      >
        {/* Product Title - Centered Design */}
        <div
          style={{
            width: '100%',
            maxWidth: '1200px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'baseline',
            gap: '10px',
            marginBottom: '20px',
            marginTop: '30px',
            padding: '0',
          }}
        >
          <Text
            size="28px"
            fw={700}
            lh={1.2}
            style={{
              textAlign: 'center',
            }}
          >
            {product.productName}
          </Text>
          {productOwner && productOwner.companyName && (
            <Text
              size="xs"
              c="dimmed"
              lh={1}
              style={{
                fontStyle: 'italic',
              }}
            >
              by {productOwner.companyName}
            </Text>
          )}
          {/* Temporarily hidden — categories UI disabled
          {product.category ? (
            <CategoryBadge
              category={product.category}
              size="sm"
              onClick={() => handleCategoryNavigate(product.category!)}
            />
          ) : null}
          */}
        </div>

        {/* Owner bar: edit shortcut + visibility notice. Owner-only UX; never rendered in embeds.
            Server-side auth is unaffected (getProductForEdit 403s non-owners regardless). */}
        {isOwner && !isIframe && id && (
          product.isPublic === false ? (
            <Paper
              withBorder
              radius="md"
              p="sm"
              style={{
                width: '100%',
                maxWidth: '1300px',
                marginBottom: '20px',
                backgroundColor: 'rgba(250, 176, 5, 0.08)',
                borderColor: 'rgba(250, 176, 5, 0.45)',
              }}
            >
              <Group justify="space-between" gap="sm" wrap="wrap">
                <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
                  <IconEyeOff size={18} style={{ flexShrink: 0 }} />
                  <Text size="sm">
                    {t('productViewer.ownerBar.notPublic')}{' '}
                    <Text span size="sm" c="dimmed">
                      {t('productViewer.ownerBar.linkAccess', {
                        mode:
                          product.linkShareMode === 'password'
                            ? t('editProduct.linkShare.modePassword')
                            : product.linkShareMode === 'restricted'
                              ? t('editProduct.linkShare.modeRestricted')
                              : t('editProduct.linkShare.modeOpen'),
                      })}
                    </Text>
                  </Text>
                </Group>
                <Group gap="xs" wrap="nowrap">
                  <Button
                    size="xs"
                    variant="default"
                    onClick={() => setShareModalOpened(true)}
                  >
                    {t('productViewer.ownerBar.shareSettings')}
                  </Button>
                  <Button
                    size="xs"
                    variant="light"
                    color={corporateColor}
                    component="a"
                    href={`${clientRoutes.editproduct}?id=${id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    leftSection={<IconPencil size={14} />}
                  >
                    {t('productViewer.ownerBar.editProduct')}
                  </Button>
                </Group>
              </Group>
            </Paper>
          ) : (
            <Group
              justify="flex-end"
              style={{ width: '100%', maxWidth: '1300px', marginBottom: '12px' }}
            >
              <Button
                size="xs"
                variant="light"
                color={corporateColor}
                component="a"
                href={`${clientRoutes.editproduct}?id=${id}`}
                target="_blank"
                rel="noopener noreferrer"
                leftSection={<IconPencil size={14} />}
              >
                {t('productViewer.ownerBar.editProduct')}
              </Button>
            </Group>
          )
        )}

        {/* Main Product Card */}
        <Card
          shadow="md"
          radius="md"
          withBorder
          style={{
            width: '100%',
            maxWidth: '1300px',
            position: 'relative',
            marginBottom: '20px',
            overflow: 'visible',
            padding: '0',
          }}
          p={0}
        >
          {/* View count + star badges */}
          <div
            style={{
              position: 'absolute',
              top: 15,
              right: 15,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 6,
              zIndex: 10,
            }}
          >
            <ProductCardOverlayChip size="md" icon={<IconEye size={14} stroke={2} />}>
              {product.views}
            </ProductCardOverlayChip>

            {!session.loading && productLoadStatus === 'success' && Boolean(id) && !isIframe && (
              <Tooltip label={starActionTooltip}>
                <ProductCardOverlayChip
                  size="md"
                  variant="stat"
                  icon={
                    starredByMe ? (
                      <IconStarFilled size={14} style={{ color: '#ca8a04' }} />
                    ) : (
                      <IconStar size={14} stroke={2} />
                    )
                  }
                  interactive
                  loading={starActionLoading}
                  ariaLabel={starActionTooltip}
                  onClick={() => void handleToggleStar()}
                >
                  {starCount}
                </ProductCardOverlayChip>
              </Tooltip>
            )}

            {product._id && product.metadata?.rows?.[product._id]?.data?.price && (
              <ProductCardOverlayChip size="md" icon={<IconTag size={14} stroke={2} />}>
                {product.metadata.rows[product._id].data.price}
              </ProductCardOverlayChip>
            )}
          </div>

          {/*
            Theater layout: keep `#product-viewer-stage` as a stable first child.
            Inserting a spacer/backdrop *before* the stage remounts <model-viewer>
            (React reconciles sibling divs) and causes a multi-second blank reload on exit.
            Outer shell holds the in-flow height while the stage is position:fixed.
          */}
          <div
            style={{
              position: 'relative',
              width: '100%',
              height: theaterMode ? 700 : undefined,
            }}
          >
          {/* 3D Viewer — ref used for browser fullscreen (Sketchfab-style); .product-viewer-stage :fullscreen rules in tempViewer.css */}
          <div
            id="product-viewer-stage"
            ref={viewerStageRef}
            className={[
              'product-viewer-stage',
              theaterMode ? 'product-viewer-stage--theater' : '',
              theaterMode && theaterAnimating ? 'product-viewer-stage--theater-animating' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{
              position: 'relative',
              padding: 0,
              width: '100%',
              // Keep an explicit stage size so <model-viewer height:100%> never remounts
              // when switching between card and theater — only this shell resizes.
              height: theaterMode || isFullscreen ? 'min(88vh, 920px)' : 700,
              display: theaterMode || isFullscreen ? 'flex' : 'block',
              flexDirection: theaterMode || isFullscreen ? 'column' : undefined,
              ...(theaterMode
                ? {
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    // Always include centering translate so it is never animated from 0.
                    transform: theaterEntered
                      ? 'translate(-50%, -50%) scale(1)'
                      : 'translate(-50%, -50%) scale(0.96)',
                    width: 'min(96vw, 1400px)',
                    zIndex: 2001,
                    backgroundColor: 'var(--mantine-color-body)',
                    borderRadius: '12px',
                    boxShadow: theaterEntered
                      ? '0 16px 48px rgba(0, 0, 0, 0.5)'
                      : '0 8px 24px rgba(0, 0, 0, 0.28)',
                    overflow: 'hidden',
                    cursor: 'default',
                  }
                : {}),
              ...(isFullscreen && !theaterMode
                ? {
                    backgroundColor: 'var(--mantine-color-body)',
                    width: '100%',
                    height: '100%',
                    maxWidth: 'none',
                  }
                : {}),
            }}
          >
            {/*
              Keep <model-viewer> at a fixed sibling index. Conditionally mounting
              chrome *before* it remounts the custom element (full GLB reload).
            */}
            <Box id="product-viewer-annotation-bar" style={annotationBarPortalStyle} />
            <model-viewer
              ref={modelViewerRef}
              style={{
                width: '100%',
                // Always fill the stage — theater only resizes the stage shell.
                height: '100%',
                minHeight: theaterMode || isFullscreen ? 'min(35vh, 480px)' : 700,
                flex: theaterMode || isFullscreen ? 1 : undefined,
                borderRadius: theaterMode || isFullscreen ? 0 : '8px 8px 0 0',
                overflow: 'hidden',
                display: 'block',
                position: 'relative',
                ...immersiveModelViewerStyle,
              }}
              src={
                product?.availableFormats?.includes('.glb') && glbModelSrcWithNonce
                  ? glbModelSrcWithNonce
                  : ''
              }
              ios-src={
                product?.availableFormats?.includes('.usdz') && id
                  ? withProductShareQuery(productUsdzUrl(id), shareToken)
                  : ''
              }
              ar
              ar-modes="webxr scene-viewer quick-look"
              ar-scale="auto"
              ar-placement="floor"
              interaction-prompt="auto"
              interaction-prompt-style="basic"
              {...presentationAttrs}
              camera-controls
              {...MODEL_VIEWER_RELAXED_ZOOM_ATTRS}
              touch-action="pan-y"
              enable-pan
              autoplay
              {...savedProductCameraAttrs}
            >
              {annotationsVisible &&
                annotations.map((annotation) => (
                  <AnnotationHotspot
                    key={annotation.id}
                    annotation={annotation}
                    onClick={handleAnnotationClick}
                  />
                ))}
            </model-viewer>

            {(theaterMode || isFullscreen) && (
              <ActionIcon
                variant="filled"
                color="dark"
                radius="xl"
                size="lg"
                aria-label={t('marketplace.productViewer.closeImmersive')}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  closeTheater();
                }}
                style={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  zIndex: viewerCloseChromeZ ?? 50,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.35)',
                }}
              >
                <IconX size={20} stroke={2} />
              </ActionIcon>
            )}
            {(theaterMode || isFullscreen) && showProductViewerLayoutControls && (
              <>
                <ModelViewerResetZoomButton
                  modelViewerRef={modelViewerRef}
                  cameraDefaults={{
                    cameraOrbit: initialCameraOrbit,
                    cameraTarget: initialCameraTarget,
                    cameraFieldOfView: initialCameraFieldOfView,
                  }}
                  zIndex={viewerChromeStackZ ?? 11}
                />
                <ModelViewerScreenshotButton
                  modelViewerRef={modelViewerRef}
                  fileName={product?.productName}
                  zIndex={viewerChromeStackZ ?? 11}
                />
              </>
            )}

            {/* Active Annotation Info Box - Positioned next to clicked pivot */}
            {activeAnnotation && annotationPosition && (
              <AnnotationInfoBox
                activeAnnotation={activeAnnotation}
                annotationPosition={annotationPosition}
                descriptionBoxRef={descriptionBoxRef}
                backgroundColor={corporateColor}
                onProductClick={(product) => {
                  setSelectedProduct(product);
                  setProductModalOpened(true);
                }}
                onImageClick={() => setImageModalOpened(true)}
              />
            )}
            {/* Custom AR button - only show on desktop (JavaScript detection) */}
            {isDesktop && (
              <div
                style={{
                  position: 'absolute',
                  bottom: '16px',
                  right: '16px',
                  zIndex: viewerChromeStackZ ?? 11,
                  display: 'flex',
                  gap: '8px',
                }}
                className="desktop-ar-buttons"
              >
                <ViewerArButton
                  accentColor={DEFAULT_QR_CODE_COLOR}
                  onClick={handleARClick}
                />
              </div>
            )}

            {/* Animation + Annotation buttons: single row so they never overlap */}
            <Box
              style={{
                position: 'absolute',
                bottom: '16px',
                right: '116px', // Play button (68px) + button width (40px) + gap (8px) = 116px
                zIndex: viewerChromeStackZ ?? 12,
                display: 'flex',
                flexDirection: 'row',
                gap: '8px',
                alignItems: 'center',
              }}
            >
              <ModelViewerPresentationPanel
                settings={presentationSettings}
                onChange={setPresentationSettings}
                modelViewerRef={modelViewerRef}
                color={corporateColor}
                inline
                immersiveLayerBaseZ={viewerImmersiveChromeBaseZ}
                menuPortalProps={
                  portalIntoViewerStage ? { target: viewerStagePortalSelector } : undefined
                }
              />
              <AnnotationNavigator
                ref={annotationNavigatorRef}
                annotations={annotations}
                modelViewerRef={modelViewerRef}
                color={corporateColor}
                onAnnotationChange={setActiveAnnotation}
                onAnnotationsVisibleChange={setAnnotationsVisible}
                hasAnimationSelector={hasAnimations}
                inline={true}
                barPortalTargetId="product-viewer-annotation-bar"
                immersiveLayerBaseZ={viewerImmersiveChromeBaseZ}
                menuPortalProps={
                  portalIntoViewerStage ? { target: viewerStagePortalSelector } : undefined
                }
              />
              <AnimationSelector
                modelViewerRef={modelViewerRef}
                color={corporateColor}
                immersiveLayerBaseZ={viewerImmersiveChromeBaseZ}
                menuPortalProps={
                  portalIntoViewerStage ? { target: viewerStagePortalSelector } : undefined
                }
              />
            </Box>

            <ViewerPlayPauseButton
              accentColor={corporateColor}
              isPlaying={isPlaying}
              onClick={toggleAnimation}
              className="play-pause-button"
              style={{
                position: 'absolute',
                bottom: '16px',
                right: '68px',
                zIndex: viewerChromeStackZ ?? 11,
              }}
            />

            {showModelViewerHelp && (
              <>
                <ModelViewerHelpButton
                  onClick={() => setHelpOverlayOpened(true)}
                  zIndex={viewerChromeStackZ ?? 12}
                />
                <ModelViewerHelpOverlay
                  opened={helpOverlayOpened}
                  onClose={() => setHelpOverlayOpened(false)}
                  onResetCamera={handleResetProductView}
                  features={modelViewerHelpFeatures}
                  zIndex={viewerHelpOverlayZ}
                />
              </>
            )}
          </div>

          {theaterMode ? (
            <Box
              onClick={closeTheater}
              className={`product-viewer-theater-backdrop${theaterEntered ? ' is-entered' : ''}`}
              aria-hidden
            />
          ) : null}
          </div>

          {/* Product Info Section */}
          <Card.Section p="xl">
            <Group justify="space-between" align="center" style={{ gap: '15px' }}>
              <Group gap="lg">
                {productOwner && (
                  <Group gap="md">
                    {productOwner.companyLogo && (
                      <img
                        src={`${cdnCompanyLogos}${productOwner.companyLogo}`}
                        height="40px"
                        alt="Company Logo"
                        style={{ borderRadius: '4px', maxWidth: '90px' }}
                      />
                    )}
                    <div>
                      {productOwner.companyName && (
                        <Text fw={500} size="sm">
                          {productOwner.companyName}
                        </Text>
                      )}
                      {productOwner.username && (
                        <Text
                          size="xs"
                          c="dimmed"
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            window.location.href = `${websiteDomain}/user/${productOwner.username}`;
                          }}
                        >
                          @{productOwner.username}
                        </Text>
                      )}
                    </div>
                  </Group>
                )}
              </Group>

              <Group gap="md" align="center">
                <InteractionBar
                  showQR={showQRCode}
                  toggleQR={() => setShowQRCode(!showQRCode)}
                  corporateColor={corporateColor}
                  showViewerLayoutControls={showProductViewerLayoutControls}
                  theaterMode={theaterMode}
                  onToggleTheater={() => (theaterMode ? closeTheater() : openTheater())}
                  onToggleFullscreen={() => toggleViewerFullscreen(viewerStageRef.current)}
                  isFullscreen={isFullscreen}
                  showResetView={showProductViewerLayoutControls}
                  onResetView={handleResetProductView}
                  showScreenshot={showProductViewerLayoutControls}
                  onScreenshot={handleProductScreenshot}
                  screenshotBusy={screenshotBusy}
                  showShareLink={!isIframe && productLoadStatus === 'success' && Boolean(id)}
                  onShareLink={() => setShareModalOpened(true)}
                  showCompare={!isIframe && productLoadStatus === 'success' && Boolean(id)}
                  compareSelected={compareSelected}
                  onCompare={handleCompareToggle}
                />
              </Group>
            </Group>
          </Card.Section>
        </Card>

        {/* Freyler company special info section */}
        {productOwner && productOwner.companyName?.toLowerCase().includes('freyler') && (
          <Paper
            p="md"
            shadow="sm"
            radius="md"
            withBorder
            style={{
              width: '100%',
              maxWidth: '1300px',
              marginBottom: '20px',
              backgroundColor: 'rgba(250, 250, 250, 0.9)',
              borderColor: '#eaeaea',
            }}
          >
            <Text ta="center" size="sm" style={{ lineHeight: 1.6 }}>
              <span style={{ fontWeight: 500 }}>
                Sie planen ein Bauprojekt? Lassen Sie sich beraten!
              </span>{' '}
              <br />
              <Anchor
                href="mailto:nuernberg@freyler.de"
                underline="hover"
                style={{ color: corporateColor }}
              >
                nuernberg@freyler.de
              </Anchor>{' '}
              I Tel.{' '}
              <Anchor href="tel:+4915172012597" underline="hover" style={{ color: corporateColor }}>
                +49 (0) 15172012597
              </Anchor>{' '}
              <br />
              <Anchor
                href="https://www.freyler.de"
                target="_blank"
                underline="hover"
                style={{ color: corporateColor, fontWeight: 700 }}
              >
                www.freyler.de
              </Anchor>
            </Text>
          </Paper>
        )}

        {/* Product Specifications Accordion */}
        {product._id &&
          product.metadata?.rows?.[product._id]?.data &&
          Object.keys(product.metadata.rows[product._id].data).length > 0 && (
            <Accordion
              variant="separated"
              radius="md"
              defaultValue="specs"
              style={{
                width: '100%',
                maxWidth: '1300px',
                marginTop: '20px',
                marginBottom: '20px',
              }}
            >
              <Accordion.Item value="specs">
                <Accordion.Control
                  icon={<IconList size={20} />}
                  style={{
                    padding: '16px 20px',
                    fontSize: '1rem',
                    fontWeight: 600,
                  }}
                >
                  <Text fw={600} size="l">
                    Details
                  </Text>
                </Accordion.Control>
                <Accordion.Panel style={{ padding: '24px' }}>
                  <DataViewer
                    data={product.metadata.rows[product._id!].data}
                    corporateColor={corporateColor}
                    onTagNavigate={handleTagNavigate}
                  />
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          )}

        {product._id &&
          product.descriptionHtml &&
          hasVisibleProductDescription(product.descriptionHtml) && (
            <Accordion
              variant="separated"
              radius="md"
              defaultValue="description"
              style={{
                width: '100%',
                maxWidth: '1300px',
                marginTop: '20px',
                marginBottom: '20px',
              }}
            >
              <Accordion.Item value="description">
                <Accordion.Control
                  icon={<IconFileDescription size={20} />}
                  style={{
                    padding: '16px 20px',
                    fontSize: '1rem',
                    fontWeight: 600,
                  }}
                >
                  <Text fw={600} size="l">
                    {t('productViewer.description')}
                  </Text>
                </Accordion.Control>
                <Accordion.Panel style={{ padding: '24px' }}>
                  <ProductDescriptionHtml html={product.descriptionHtml} />
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          )}

        {product._id && productOwner?.companyName ? (
          <RelatedProductsSection
            productId={product._id}
            companyName={productOwner.companyName}
            tags={extractProductTags({
              _id: product._id,
              tags: product.tags,
              metadata: product.metadata,
            })}
            // Temporarily hidden — categories UI disabled
            // category={product.category}
            accentColor={corporateColor}
          />
        ) : null}

        {qrModal}

        <Modal
          opened={starLoginModalOpened}
          onClose={() => setStarLoginModalOpened(false)}
          title={t('marketplace.productViewer.starLoginTitle')}
          centered
          zIndex={viewerChromeStackZ}
        >
          <Text size="sm">{t('marketplace.productViewer.starLoginMessage')}</Text>
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setStarLoginModalOpened(false)}>
              {t('marketplace.productViewer.starLoginCancel')}
            </Button>
            <Button
              color={corporateColor}
              onClick={() => {
                if (!id) return;
                sessionStorage.setItem(PENDING_PRODUCT_STAR_STORAGE_KEY, id);
                setStarLoginModalOpened(false);
                const returnPath = `${location.pathname}${location.search}`;
                navigate(`${clientRoutes.login}?returnTo=${encodeURIComponent(returnPath)}`);
              }}
            >
              {t('marketplace.productViewer.starLoginConfirm')}
            </Button>
          </Group>
        </Modal>

        <ProductShareModal
          opened={shareModalOpened && Boolean(id)}
          onClose={() => setShareModalOpened(false)}
          productId={id ?? ''}
          productName={product.productName}
          linkShareMode={(product.linkShareMode as LinkShareMode) ?? 'open'}
          isOwner={isOwner}
          accentColor={corporateColor}
          qrLogoUrl={
            productOwner?.companyLogo
              ? `${cdnCompanyLogos}${productOwner.companyLogo}`
              : undefined
          }
        />
      </Center>
    </>
  );
}
